import { Component, ElementRef, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Site } from '../../../../models/site';
import { MatDialog } from '@angular/material/dialog';
import { first } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';
import {
  retrieveDenseCloudDownloadUrl,
  retrieveDenseCloudDownloadUrlFailure,
  retrieveDenseCloudDownloadUrlSuccess,
  retrieveSparseCloudDownloadUrl,
  retrieveSparseCloudDownloadUrlFailure,
  retrieveSparseCloudDownloadUrlSuccess
} from '../../../../features/batch/batch.actions';
import { ExportTools } from '../../../../tools/export.tools';
import { User } from '../../../../models/user';
import { DatePipe } from '@angular/common';
import { selectUser } from '../../../../features/user/user.selectors';
import {
  addCuts,
  addCutsFailure,
  addCutsSuccess,
  generateCutsDXFFailure,
  generateCutsDXFSuccess,
  generateCutsReportFailure,
  generateCutsReportSuccess,
  retrieveCutsCoordinatesCSVFailure,
  retrieveCutsCoordinatesCSVSuccess
} from '../../../../features/cut/cut.actions';
import {
  addTrackerElevations,
  addTrackerElevationsFailure,
  addTrackerElevationsSuccess, addTrackerImages,
  addTrackerImagesFailure,
  addTrackerImagesSuccess,
  retrieveElevationsCoordinatesCSVFailure,
  retrieveElevationsCoordinatesCSVSuccess,
  retrieveMarkersCoordinatesCSVFailure,
  retrieveMarkersCoordinatesCSVSuccess
} from '../../../../features/tracker/tracker.actions';
import { InspectionModule } from '../../../../enums/inspection-module';
import {
  selectAdvancedFeaturesActivating,
  selectAdvancedFeaturesAvailable, selectAdvancedFeaturesExpirationDate,
  selectCurrentSite
} from '../../../../features/site/site.selectors';
import {
  AdvandcedActionStartDialog
} from '../../../../shared/dialogs/advandced-action-start-dialog/advandced-action-start.dialog';
import {
  activateAdvancedFeatures,
  activateAdvancedFeaturesFailure,
  activateAdvancedFeaturesSuccess,
} from '../../../../features/site/site.actions';
import { UploadType } from '../../../../enums/upload';
import {
  CsvReaderDialog,
  CsvReaderMode
} from '../../../../shared/dialogs/csv-reader-dialog/csv-reader.dialog';
import {
  generateDetectionsReportFailure,
  generateDetectionsReportSuccess
} from '../../../../features/detection/detection.actions';
import { SiteStatus } from '../../../../enums/site';
import {
  generateVolumesDXFFailure, generateVolumesDXFSuccess
} from '../../../../features/volume/volume.actions';
import {
  SiteSettingsDialogComponent
} from '../site-settings-dialog/site-settings-dialog.component';
import {
  selectCurrentOrganization
} from '../../../../features/organization/organization.selectors';
import { Organization } from '../../../../models/organization';
import { UserTools } from '../../../../tools/user.tools';
import { UserRole } from '../../../../enums/user-role';


@Component({
    selector: 'site-actions-bar',
    templateUrl: './site-actions-bar.component.html',
    styleUrls: ['./site-actions-bar.component.scss'],
    standalone: false
})
export class SiteActionsBarComponent {

  siteStatus = SiteStatus
  inspectionModule = InspectionModule;
  isRequestingAdvancedFeaturesActivation = false;
  @ViewChild('fileUpload') fileUploadRef: ElementRef<HTMLInputElement> | undefined;
  private uploadType: UploadType = UploadType.NONE;

  readonly site$ = this.store.select(selectCurrentSite);
  readonly advancedFeaturesAvailable$ = this.store.select(selectAdvancedFeaturesAvailable);
  readonly advancedFeaturesActivating$ = this.store.select(selectAdvancedFeaturesActivating);
  readonly advancedFeaturesExpirationDate$ = this.store.select(selectAdvancedFeaturesExpirationDate);
  readonly user$ = this.store.select(selectUser);
  readonly organization$ = this.store.select(selectCurrentOrganization);

  constructor(private store: Store,
              private actions: Actions,
              private dialog: MatDialog,
              private translocoService: TranslocoService,
              private toastr: ToastrService) {
    this.initHandleReport();
    this.initHandleDownload();
    this.initHandleCSV();
    this.initHandleAdvancedFeatures();
  }

  private initHandleAdvancedFeatures() {
    this.actions.pipe(
      ofType(activateAdvancedFeatures),takeUntilDestroyed())
      .subscribe(result => {
        this.isRequestingAdvancedFeaturesActivation = true;
      });

    this.actions.pipe(
      ofType(activateAdvancedFeaturesSuccess, activateAdvancedFeaturesFailure),
      takeUntilDestroyed())
      .subscribe(result => {
        this.isRequestingAdvancedFeaturesActivation = false;
      });
  }


  private initHandleDownload(): void {
    this.actions.pipe(ofType(retrieveSparseCloudDownloadUrlSuccess), takeUntilDestroyed())
      .subscribe((result: any) => {
        ExportTools.exportResourceUrl(result.url);
      });
    this.actions.pipe(ofType(retrieveDenseCloudDownloadUrlSuccess), takeUntilDestroyed())
      .subscribe((result: any)  => {
        ExportTools.exportResourceUrl(result.url);
      });
    this.actions.pipe(ofType(retrieveSparseCloudDownloadUrlFailure, retrieveDenseCloudDownloadUrlFailure), takeUntilDestroyed())
      .subscribe((result: any)  => {
        this.toastr.error(this.translocoService.translate('process_error'));
      });
  }

  private initHandleCSV(): void {
    this.actions.pipe(ofType(addCutsSuccess, addTrackerImagesSuccess, addTrackerElevationsSuccess), takeUntilDestroyed())
      .subscribe((result: any)  => {
        this.toastr.success(this.translocoService.translate('csv_content_add_success'));
      });
    this.actions.pipe(ofType(addCutsFailure, addTrackerElevationsFailure, addTrackerImagesFailure), takeUntilDestroyed())
      .subscribe((result: any)  => {
        this.toastr.error(this.translocoService.translate('csv_content_add_failure'));
      });
    this.actions.pipe(ofType(retrieveCutsCoordinatesCSVSuccess), takeUntilDestroyed())
      .subscribe((result: any)  => {
        this.site$.pipe(first())
          .subscribe((site: Site | undefined | null) => {
          if (site) {
            const filename = site.name + '_cuts_coordinates';
            ExportTools.exportCSV(filename, result.csv);
          }
        });
      });
    this.actions.pipe(ofType(retrieveMarkersCoordinatesCSVSuccess), takeUntilDestroyed())
      .subscribe(result => {
        this.site$.pipe(first())
          .subscribe((site: Site | undefined | null) => {
          if (site) {
            const filename = site.name + '_markers_coordinates';
            ExportTools.exportCSV(filename, result.csv);
          }
        });
      });
    this.actions.pipe(ofType(retrieveElevationsCoordinatesCSVSuccess), takeUntilDestroyed())
      .subscribe(result => {
        this.site$.pipe(first())
          .subscribe((site: Site | undefined | null) => {
            if (site) {
              const filename = site.name + '_elevations_coordinates';
              ExportTools.exportCSV(filename, result.csv);
            }
          });
      });
    this.actions.pipe(ofType(
      retrieveCutsCoordinatesCSVFailure,
      retrieveMarkersCoordinatesCSVFailure,
      retrieveElevationsCoordinatesCSVFailure), takeUntilDestroyed())
      .subscribe((result: any)  => {
        this.toastr.error(this.translocoService.translate('process_error'));
      });
  }

  private initHandleReport(): void {
    this.actions.pipe(ofType(
      generateDetectionsReportSuccess,
        generateCutsReportSuccess,
        generateCutsDXFSuccess,
        generateVolumesDXFSuccess),
      takeUntilDestroyed())
      .subscribe(data => {
        this.toastr.success(this.translocoService.translate('generate_report_success', {mail: data.email}));
      });
    this.actions.pipe(ofType(
        generateDetectionsReportFailure,
        generateCutsReportFailure,
        generateCutsDXFFailure,
        generateVolumesDXFFailure),
      takeUntilDestroyed())
      .subscribe(result => {
        this.toastr.error(this.translocoService.translate('generate_report_failure'));
      });
  }

  onClickDownloadPointCloudDense(site: Site) {
    this.store.select(selectUser)
      .pipe(first())
      .subscribe((user: User | null | undefined) => {
        if (site && user) {
          const dateFormat = user.languageCode == 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
          const datePipe: DatePipe = new DatePipe(this.translocoService.getActiveLang());
          const date = datePipe.transform(new Date(site.lastBatchDate), dateFormat)
          const filename = site.name.replace(/ /g, "_") + '_' + date + '_dense_cloud.ply';
          this.store.dispatch(retrieveDenseCloudDownloadUrl({
            organizationId: site.organization,
            siteId: site.id,
            batchId: site.lastBatch,
            filename
          }));
        }
      });
  }

  onClickDownloadPointCloudSparse(site: Site) {
    this.store.select(selectUser)
      .pipe(first())
      .subscribe((user: User | null | undefined) => {
        if (site && user) {
          const dateFormat = user.languageCode == 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
          const datePipe: DatePipe = new DatePipe(this.translocoService.getActiveLang());
          const date = datePipe.transform(new Date(site.lastBatchDate), dateFormat)
          const filename = site.name.replace(/ /g, "_") + '_' + date + '_sparse_cloud.ply';
          this.store.dispatch(retrieveSparseCloudDownloadUrl({
            organizationId: site.organization,
            siteId: site.id,
            batchId: site.lastBatch,
            filename
          }));
        }
      });
  }

  activate(site: Site) {
    const dialogRef = this.dialog.open(AdvandcedActionStartDialog);
    dialogRef.afterClosed().subscribe(result => {
      if(result) {
        this.store.dispatch(activateAdvancedFeatures({
          organizationId: site.organization,
          siteId: site.id
        }));
      }
    });
  }

  onClickUploadCSVCut(site: Site): void {
    this.uploadType = UploadType.CSV_CUT;
    this.openFileChooser("text/csv");
  }

  onClickUploadCSVElevation(site: Site): void {
    this.uploadType = UploadType.CSV_ELEVATION;
    this.openFileChooser("text/csv");
  }

  private openFileChooser(accept: string) {
    this.fileUploadRef?.nativeElement.setAttribute('accept',"text/csv" );
    this.fileUploadRef?.nativeElement.click();
  }

  onFileSelected(event: any): void {
    const file:File = event.target.files[0];
    if (file) {
      this.site$.pipe(first())
        .subscribe((site: Site | undefined | null) => {
          if(!site)
            return
          file.text().then((content: any) => {
            const csvReaderDialogRef = this.dialog.open(CsvReaderDialog);
            csvReaderDialogRef.componentInstance.content = content;
            csvReaderDialogRef.componentInstance.mode = (this.uploadType == UploadType.CSV_CUT) ? CsvReaderMode.TWO_POINTS : CsvReaderMode.ONE_POINT;
            csvReaderDialogRef
              .afterClosed()
              .subscribe(data => {
                if (data && site) {
                  switch (this.uploadType) {
                    case UploadType.CSV_CUT:
                      this.store.dispatch(addCuts({
                        organizationId: site.organization,
                        siteId: site.id,
                        cuts: data
                      }));
                      break;
                    case UploadType.CSV_ELEVATION:
                      this.store.dispatch(addTrackerElevations({
                        organizationId: site.organization,
                        siteId: site.id,
                        trackers: data,
                      }));
                      break;
                    case UploadType.CSV_MARKER:
                      this.store.dispatch(addTrackerImages({
                        organizationId: site.organization,
                        siteId: site.id,
                        trackers: data,
                      }));
                      break;
                  }
                }
              });
          });
        });
    }
  }

  onClickSettings(
    site: Site,
    user: User,
    organization: Organization,
  ): void {
    this.dialog.open(SiteSettingsDialogComponent, {
      width: '860px',
      maxWidth: '92vw',
      height: '480px',
      maxHeight: '85vh',
      data: {
        site,
        user,
        organization,
      },
    });
  }

  canAccessSettings(
    user: User,
    organization: Organization,
  ): boolean {
    return UserTools.hasAnyRole(
      user,
      organization,
      [
        UserRole.BASIC,
        UserRole.ADMIN,
      ],
    );
  }

}
